import ClearIcon from "@mui/icons-material/Clear";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import {
  Alert,
  AlertTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { FullScreenDocumentPreview, extensionFromName } from "components/Document/shared";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useMemo, useState } from "react";

import { ScoutFilePart } from "./scoutChatMessage";

const UPLOADED_FILE_URL_PREFIX = "encamp-upload://";

function parseUploadStorageKey(url: string | undefined): string | undefined {
  if (!url?.startsWith(UPLOADED_FILE_URL_PREFIX)) {
    return undefined;
  }
  const storageKey = url.slice(UPLOADED_FILE_URL_PREFIX.length);
  return storageKey.length > 0 ? storageKey : undefined;
}

const userFriendlyMediaType = (mediaType: string): string => {
  switch (mediaType) {
    case "application/pdf":
      return "PDF";
    case "image/png":
      return "PNG";
    case "image/jpeg":
      return "JPEG";
    default:
      return mediaType;
  }
};

type Props = {
  part: ScoutFilePart;
  onRemove?: () => void;
  state?: "default" | "error";
  errorText?: string;
};

export const ScoutFileDisplay = ({ part, onRemove, state = "default", errorText }: Props) => {
  const theme = useTheme();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const { fetchFile } = useCheckForFile();

  const uploadStorageKey = useMemo(() => parseUploadStorageKey(part.url), [part.url]);
  const historySignedUrl = part.document?.signedDownloadUrl;
  const canPreview = historySignedUrl !== undefined || uploadStorageKey !== undefined;
  const fileExtension = extensionFromName(part.filename ?? part.title ?? "");

  const resolveSignedPreviewUrl = useCallback(async (): Promise<string | undefined> => {
    if (historySignedUrl) {
      return historySignedUrl;
    }
    if (uploadStorageKey) {
      return (await fetchFile(uploadStorageKey)) ?? undefined;
    }
    return undefined;
  }, [fetchFile, historySignedUrl, uploadStorageKey]);

  const openPreview = useCallback(async () => {
    if (!canPreview || isPreviewLoading) {
      return;
    }
    if (previewUrl) {
      setIsPreviewOpen(true);
      return;
    }
    if (historySignedUrl) {
      setPreviewUrl(historySignedUrl);
      setIsPreviewOpen(true);
      return;
    }
    setIsPreviewLoading(true);
    try {
      const signedUrl = await resolveSignedPreviewUrl();
      if (!signedUrl) {
        return;
      }
      setPreviewUrl(signedUrl);
      setIsPreviewOpen(true);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [canPreview, historySignedUrl, isPreviewLoading, previewUrl, resolveSignedPreviewUrl]);

  const isError = state === "error";
  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ width: "100%" }}
        action={
          onRemove ? (
            <Tooltip title="Remove invalid file">
              <IconButton
                edge="end"
                size="small"
                aria-label={`remove-file-${part.id}`}
                onClick={onRemove}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      >
        <AlertTitle>{`Error: ${part.title ?? part.filename ?? "Attachment"}`}</AlertTitle>
        <Typography variant="body2">{errorText ?? "Upload failed."}</Typography>
      </Alert>
    );
  }

  const secondaryText = userFriendlyMediaType(part.mediaType ?? "");
  return (
    <>
      <ListItem
        dense
        onClick={canPreview ? () => void openPreview() : undefined}
        sx={{
          bgcolor: theme.palette.encampBlue.light,
          borderRadius: 2,
          cursor: canPreview ? "pointer" : "default",
          opacity: isPreviewLoading ? 0.8 : 1,
        }}
        slotProps={{ secondaryAction: { sx: { top: "24px", right: "8px" } } }}
        secondaryAction={
          <Stack direction="row" spacing={0.5}>
            {onRemove ? (
              <Tooltip title="Remove file">
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`remove-file-${part.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        }
      >
        <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
          <FileIcon />
        </ListItemIcon>
        <ListItemText
          primary={part.title ?? part.filename ?? "Attachment"}
          secondary={secondaryText}
        />
      </ListItem>
      {previewUrl ? (
        <FullScreenDocumentPreview
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={previewUrl}
          fileExtension={fileExtension}
          title={part.title ?? part.filename ?? "Attachment Preview"}
        />
      ) : null}
    </>
  );
};
